/**
 * Pure i18n: translations, locale state, API bilingual presets, calendar weekday labels.
 * No React. RTL layout isolation lives in `./rtl`.
 *
 * Boot: call `enableRtlSupport()` once from `index.js` before React mounts.
 * Locale changes: `setLocale` may `forceRTL`; `I18nProvider` persists + restarts the app.
 *
 * Web (PWA): RTL is applied via `document.documentElement.dir` immediately; restart uses
 * `location.reload`. Native iOS/Android paths below are unchanged.
 */
import { I18nManager, NativeModules } from 'react-native';
import moment from 'moment';
import en from '../../locales/en.json';
import ar from '../../locales/ar.json';
import { isWebRuntime } from '../platform/runtimeTarget';

export type LocaleCode = 'en' | 'ar';

export type GetApiTextOptions = {
  /** Override app locale for this pick only (brand/legal fields). */
  locale?: LocaleCode;
};

const translations: Record<LocaleCode, Record<string, unknown>> = {
  en: en as Record<string, unknown>,
  ar: ar as Record<string, unknown>,
};

let currentLocale: LocaleCode = 'en';

function applyWebDocumentDirection(locale: LocaleCode): void {
  if (typeof document === 'undefined') {
    return;
  }
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = locale;
  if (document.body) {
    document.body.dir = dir;
  }
}

/** Call once at process startup (before React render). Required on Android with `forceRTL`. */
export function enableRtlSupport(): void {
  if (isWebRuntime()) {
    // Web: dir is owned by setLocale / documentElement; allowRTL is a no-op.
    applyWebDocumentDirection(currentLocale);
    return;
  }
  I18nManager.allowRTL(true);
}

function getNested(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : undefined;
}

export function interpolateTranslation(
  template: string,
  params?: Record<string, string | number>,
): string {
  if (!params) {
    return template;
  }
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const value = params[key];
    if (value == null) {
      return `{{${key}}}`;
    }
    // Display numbers follow locale digits (and app_config `number_digits`); strings stay as-is (phones, ids).
    return typeof value === 'number' ? formatNumber(value) : String(value);
  });
}

export function t(key: string, params?: Record<string, string | number>): string {
  if (key == null || typeof key !== 'string') {
    return '';
  }
  const value = getNested(translations[currentLocale] as Record<string, unknown>, key);
  const template = value ?? getNested(translations.en as Record<string, unknown>, key) ?? key;
  return interpolateTranslation(template, params);
}

/** Pick singular vs `*_other` locale keys (existing JSON convention). */
export function tPlural(
  base_key: string,
  count: number,
  params?: Record<string, string | number>,
): string {
  const key = count === 1 ? base_key : `${base_key}_other`;
  return t(key, { count, ...params });
}

export function getLocale(): LocaleCode {
  return currentLocale;
}

/**
 * Updates module locale and syncs native RTL flag when needed.
 * Returns whether `forceRTL` was applied (caller may need a restart for Yoga to match).
 * Does not persist — user-facing changes go through `I18nProvider.setLocale`.
 *
 * Web: applies `document.dir` immediately and returns `did_force_rtl: false` so cold start
 * never enters an I18nManager-mismatch → reload loop (RN-web `isRTL` is unreliable).
 */
export function setLocale(locale: LocaleCode): { did_force_rtl: boolean } {
  currentLocale = locale;

  if (isWebRuntime()) {
    applyWebDocumentDirection(locale);
    // Best-effort sync for any RN-web consumers; do not treat as restart-required.
    try {
      I18nManager.allowRTL?.(true);
      const want_rtl = locale === 'ar';
      if (typeof I18nManager.forceRTL === 'function' && I18nManager.isRTL !== want_rtl) {
        I18nManager.forceRTL(want_rtl);
      }
    } catch {
      // ignore — document.dir is the source of truth on web
    }
    return { did_force_rtl: false };
  }

  const want_rtl = locale === 'ar';
  const did_force_rtl = I18nManager.isRTL !== want_rtl;
  if (did_force_rtl) {
    I18nManager.forceRTL(want_rtl);
  }
  return { did_force_rtl };
}

/** True when native RTL flag matches the given locale after `setLocale` / `forceRTL`. */
export function isNativeRtlMatchingLocale(locale: LocaleCode = currentLocale): boolean {
  if (isWebRuntime()) {
    if (typeof document !== 'undefined') {
      return document.documentElement.dir === (locale === 'ar' ? 'rtl' : 'ltr');
    }
    return locale !== 'ar';
  }
  return I18nManager.isRTL === (locale === 'ar');
}

export function getLocaleDisplayName(locale: LocaleCode): string {
  return locale === 'ar' ? 'العربية' : 'English';
}

/**
 * Pick API-sourced bilingual text by app locale (or optional override).
 * Returns preferred language value; falls back to the other key if missing/empty.
 */
export function getApiText(
  obj: Record<string, unknown> | null | undefined,
  en_key: string,
  ar_key: string,
  options?: GetApiTextOptions,
): string {
  if (obj == null || typeof obj !== 'object') {
    return '';
  }
  const en_val = obj[en_key];
  const ar_val = obj[ar_key];
  const en_str = typeof en_val === 'string' ? en_val : '';
  const ar_str = typeof ar_val === 'string' ? ar_val : '';
  const loc = options?.locale ?? currentLocale;
  return loc === 'ar' ? ar_str || en_str : en_str || ar_str;
}

/** Menu / food item display name (`name` / `a_name`; APIs may have folded `name_ar` → `a_name`). */
export function getApiName(obj: Record<string, unknown> | null | undefined, options?: GetApiTextOptions): string {
  return getApiText(obj, 'name', 'a_name', options);
}

export function getApiDescription(obj: Record<string, unknown> | null | undefined, options?: GetApiTextOptions): string {
  return getApiText(obj, 'description', 'a_description', options);
}

/** Title: tries `a_title` then `arabic_title`. */
export function getApiTitle(obj: Record<string, unknown> | null | undefined, options?: GetApiTextOptions): string {
  return (
    getApiText(obj, 'title', 'a_title', options) ||
    getApiText(obj, 'title', 'arabic_title', options)
  );
}

export function getApiTypeName(obj: Record<string, unknown> | null | undefined, options?: GetApiTextOptions): string {
  return getApiText(obj, 'type_name', 'a_type_name', options);
}

/** Currency: `a_currency` then `arabic_currency`. */
export function getApiCurrency(obj: Record<string, unknown> | null | undefined, options?: GetApiTextOptions): string {
  return (
    getApiText(obj, 'currency', 'a_currency', options) ||
    getApiText(obj, 'currency', 'arabic_currency', options)
  );
}

export function getApiBannerTitle(obj: Record<string, unknown> | null | undefined, options?: GetApiTextOptions): string {
  return getApiText(obj, 'title', 'arabic_title', options);
}

export function getApiBannerDescription(
  obj: Record<string, unknown> | null | undefined,
  options?: GetApiTextOptions,
): string {
  return getApiText(obj, 'description', 'arabic_description', options);
}

/** Format number for display; uses Arabic-Indic numerals when locale is ar (unless app_config `number_digits` is `western`). */
export type FormatNumberOptions = {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  /** Default true. Set false for years / IDs so `2026` stays `2026`, not `2,026`. */
  useGrouping?: boolean;
};

function usesWesternDigitsSafe(): boolean {
  try {
    // Lazy import avoids hard circular deps at module init.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { usesWesternDigits } = require('../../data/domains/config/appConfig') as {
      usesWesternDigits: () => boolean;
    };
    return usesWesternDigits();
  } catch {
    return false;
  }
}

function displayNumberLocale(): string {
  return currentLocale === 'ar' && !usesWesternDigitsSafe() ? 'ar-SA' : 'en-US';
}

export function formatNumber(value: number | string, options?: FormatNumberOptions): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (typeof n !== 'number' || Number.isNaN(n)) {
    return String(value);
  }
  const is_int = Number.isInteger(n);
  const maximumFractionDigits = options?.maximumFractionDigits ?? (is_int ? 0 : 2);
  const minimumFractionDigits = options?.minimumFractionDigits ?? 0;
  return n.toLocaleString(displayNumberLocale(), {
    maximumFractionDigits,
    minimumFractionDigits,
    useGrouping: options?.useGrouping ?? true,
  });
}

/** Fixed-fraction display (prices, nutrition decimals). Honors `number_digits`. */
export function formatDisplayDecimal(value: number | string, decimals = 2): string {
  return formatNumber(value, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

/** Percent label for UI text (not CSS widths). Example: `42` → `٤٢%` in ar. */
export function formatPercent(value: number | string, options?: { decimals?: number }): string {
  const decimals = options?.decimals ?? 0;
  return `${formatNumber(value, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  })}%`;
}

/**
 * Display date parts with localized month/weekday names and digit-aware day/year.
 * Keep API keys on `formatTodayForApi` / `formatMomentForApiYmd` (ASCII).
 */
export function formatDisplayDayMonth(date_input: string | Date | moment.Moment, with_weekday = false): string {
  const m = moment.isMoment(date_input)
    ? date_input.clone()
    : moment(date_input, ['YYYY-MM-DD', moment.ISO_8601], true).isValid()
      ? moment(date_input, ['YYYY-MM-DD', moment.ISO_8601], true)
      : moment(date_input);
  if (!m.isValid()) {
    return typeof date_input === 'string' ? date_input : '';
  }
  const prev = moment.locale();
  try {
    moment.locale(currentLocale === 'ar' ? 'ar' : 'en');
  } catch {
    moment.locale('en');
  }
  try {
    const month = m.format('MMM');
    const day = formatNumber(m.date());
    if (with_weekday) {
      return `${m.format('dddd')}, ${month} ${day}`;
    }
    return `${month} ${day}`;
  } finally {
    try {
      moment.locale(prev);
    } catch {
      // ignore
    }
  }
}

export function formatDisplayMonthYear(date_input: string | Date | moment.Moment): string {
  const m = moment.isMoment(date_input)
    ? date_input.clone()
    : moment(date_input);
  if (!m.isValid()) {
    return typeof date_input === 'string' ? date_input : '';
  }
  const prev = moment.locale();
  try {
    moment.locale(currentLocale === 'ar' ? 'ar' : 'en');
  } catch {
    moment.locale('en');
  }
  try {
    return `${m.format('MMMM')} ${formatNumber(m.year(), { useGrouping: false })}`;
  } finally {
    try {
      moment.locale(prev);
    } catch {
      // ignore
    }
  }
}

/**
 * Whether a full app restart API is available (language / RTL).
 * Does not schedule a restart.
 */
export function canRestartApp(): boolean {
  if (isWebRuntime()) {
    return typeof window !== 'undefined' && typeof window.location?.reload === 'function';
  }
  const native = NativeModules.RNRestart as { restart?: () => void; Restart?: () => void } | undefined;
  if (typeof (native?.restart ?? native?.Restart) === 'function') {
    return true;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const mod = require('react-native-restart') as { default?: { Restart?: () => void; restart?: () => void } };
    const api = mod?.default;
    return typeof (api?.Restart ?? api?.restart) === 'function';
  } catch {
    return false;
  }
}

/**
 * Attempt a full app restart (language / RTL). Returns true if a restart was scheduled.
 * Web: `location.reload`. Native: `NativeModules.RNRestart`, then `react-native-restart`.
 */
export function tryRestartApp(): boolean {
  if (isWebRuntime()) {
    if (typeof window === 'undefined' || typeof window.location?.reload !== 'function') {
      return false;
    }
    setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        // ignore
      }
    }, 300);
    return true;
  }
  const native = NativeModules.RNRestart as { restart?: () => void; Restart?: () => void } | undefined;
  const from_native = native?.restart ?? native?.Restart;
  if (typeof from_native === 'function') {
    setTimeout(() => {
      try {
        from_native();
      } catch {
        // ignore
      }
    }, 300);
    return true;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const mod = require('react-native-restart') as { default?: { Restart?: () => void; restart?: () => void } };
    const api = mod?.default;
    const fn = api?.Restart ?? api?.restart;
    if (typeof fn === 'function') {
      setTimeout(() => {
        try {
          fn();
        } catch {
          // ignore
        }
      }, 300);
      return true;
    }
  } catch {
    // package missing
  }
  return false;
}

// --- Calendar weekday labels (Sunday-first; matches RN calendar column index) ---

const SUNDAY_REF = '2023-01-01';

function withMomentLocale<T>(locale_code: string, run: () => T, fallback: T): T {
  const prev = moment.locale();
  const raw = (locale_code ?? 'en').toString().toLowerCase();
  const loc = raw.startsWith('ar') ? 'ar' : 'en';
  try {
    moment.locale(loc);
  } catch {
    moment.locale('en');
  }
  try {
    return run();
  } finally {
    if (typeof prev === 'string') {
      try {
        moment.locale(prev);
      } catch {
        // ignore
      }
    }
  }
}

export function getLocalizedWeekdaysShort(locale_code: string): string[] {
  return withMomentLocale(
    locale_code,
    () => {
      const sun = moment(SUNDAY_REF, 'YYYY-MM-DD', true);
      if (!sun.isValid()) {
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      }
      return [0, 1, 2, 3, 4, 5, 6].map(n => sun.clone().add(n, 'days').format('ddd'));
    },
    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  );
}

/**
 * Minimal weekday labels (Moment `dd` / weekdaysMin). Prefer this over `charAt(0)` on full Arabic names
 * (which yields invisible/clipped alef glyphs in fixed-width calendar cells).
 */
export function getLocalizedWeekdaysMin(locale_code: string): string[] {
  return withMomentLocale(
    locale_code,
    () => {
      const sun = moment(SUNDAY_REF, 'YYYY-MM-DD', true);
      if (!sun.isValid()) {
        return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
      }
      return [0, 1, 2, 3, 4, 5, 6].map(n => sun.clone().add(n, 'days').format('dd'));
    },
    ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  );
}

/** Sun=0 … Sat=6 full weekday names in the target locale. */
export function getLocalizedWeekdaysLong(locale_code: string): string[] {
  return withMomentLocale(
    locale_code,
    () => {
      const sun = moment(SUNDAY_REF, 'YYYY-MM-DD', true);
      if (!sun.isValid()) {
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      }
      return [0, 1, 2, 3, 4, 5, 6].map(n => sun.clone().add(n, 'days').format('dddd'));
    },
    ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  );
}
